/**
 * Home component
 */
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  rx,
  Observable,
  Subscription,
  observableCombineLatest,
} from '@shared/rx';
import { select } from '@angular-redux/store';
import { isEmpty, head, negate } from 'lodash';
import { XUXMenuPinnedService } from '@xenial-ux/services/xux-menu-pinned.service';
import { IMenuItem } from '@shared/types';
import { StateService, STATE_KEYS } from '@shared/services/';
import { RouterActions } from '@store/actions/';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'xvi-home',
  styleUrls: ['./home.component.scss'],
  templateUrl: './home.component.html',
})
export class HomeComponent implements OnInit, OnDestroy {
  @select(['ui', 'menuItems'])
  public sidebarItemsObs: Observable<IMenuItem[]>;

  @select(['userSession', 'userPermissions', 'items'])
  public userPermissionsObs: Observable<any>;

  @select(['userSession', 'currentCompanyId'])
  public currentCompanyIdObs: Observable<string>;

  public pinnedMenuItems: IMenuItem[] = [];
  public pinnedRoutes: string[];
  public availableRoutes: string[] = [];
  public routerHistory: string[];
  public currentCompanyId: string = null;
  public isIframeMode: boolean = null;
  public subscriptions: Subscription[] = [];

  public constructor(
    private menuPinnedService: XUXMenuPinnedService,
    private stateService: StateService,
    private routerActions: RouterActions,
  ) {
    this.currentCompanyId = this.stateService.getCurrentCompanyId();
    this.routerHistory = this.stateService.getByPath(STATE_KEYS.ROUTER_HISTORY);
    this.isIframeMode = this.stateService.getByPath(STATE_KEYS.IFRAME_MODE);
  }

  public ngOnInit(): void {
    this.subscriptions.push(
      observableCombineLatest([
        this.sidebarItemsObs.pipe(
          rx.skipUntil(
            this.userPermissionsObs.pipe(rx.filter(negate(isEmpty))),
          ),
        ),
        this.currentCompanyIdObs.pipe(
          rx.distinctUntilChanged(),
          rx.filter(this.isRedirectNecessary),
        ),
      ])
        .pipe(rx.debounceTime(0), rx.first())
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .subscribe(([items, currentCompanyId]) => {
          this.pinnedMenuItems = this.getPinnedItems(items);
          this.pinnedRoutes = this.getFlatRoutesArray(this.pinnedMenuItems);
          this.availableRoutes = this.getFlatRoutesArray(items);
          this.redirectToDefaultRoute();
        }),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  private redirectToDefaultRoute(): void {
    if (!isEmpty(this.pinnedRoutes)) {
      this.routerActions.updateLocation(head(this.pinnedRoutes));

      return;
    }

    if (!isEmpty(this.availableRoutes)) {
      const routeToRedirect = head(this.availableRoutes);

      this.routerActions.updateLocation(routeToRedirect);
    }
  }

  private getPinnedItems(sidebarItems: IMenuItem[]): IMenuItem[] {
    const pinnedMenu: string[] = this.menuPinnedService.getPinnedMenu();
    const pinnedMenuItems = [];
    const getPinnedMenuItems = (item: IMenuItem): void => {
      const { title, subItemsArray, hidden } = item;

      if (pinnedMenu.includes(title) && !hidden) pinnedMenuItems.push(item);

      if (!isEmpty(subItemsArray)) subItemsArray.forEach(getPinnedMenuItems);
    };

    sidebarItems.forEach(getPinnedMenuItems);

    return pinnedMenuItems;
  }

  private getFlatRoutesArray(sidebarItems: IMenuItem[]): string[] {
    const routes = [];

    const iterator = (sidebarItem: IMenuItem): void => {
      const { route, subItemsArray, hidden } = sidebarItem;

      if (route && !hidden) routes.push(route);

      if (!isEmpty(subItemsArray)) subItemsArray.forEach(iterator);
    };

    sidebarItems.forEach(iterator);

    return routes;
  }

  private isRedirectNecessary = (companyId: string): boolean => {
    const standAloneFilterStatement =
      isEmpty(this.routerHistory) || companyId !== this.currentCompanyId;

    return this.isIframeMode || standAloneFilterStatement;
  };
}
